import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import Suggestion from "@tiptap/suggestion"

import type {
	CloseLoopPlaceholder,
	CloseLoopTemplateKind,
} from "@productlane/lib/src/close-loop-template"
import {
	CLOSE_LOOP_PLACEHOLDERS,
	closeLoopPlaceholderToken,
} from "@productlane/lib/src/close-loop-template"

import type { Editor, Range } from "@tiptap/core"
import type { SuggestionOptions } from "@tiptap/suggestion"
import { closeLoopPlaceholderRenderer } from "./suggestion"

export const CloseLoopPlaceholderPluginKey = new PluginKey(
	"closeLoopPlaceholderSuggestion",
)

// A closed brace is the finished token, so the list steps aside.
export function filterPlaceholders(
	placeholders: CloseLoopPlaceholder[],
	query: string,
): CloseLoopPlaceholder[] {
	if (query.includes("}")) return []
	const needle = query.trim().toLowerCase()
	return placeholders.filter(
		(p) =>
			!needle ||
			p.key.toLowerCase().includes(needle) ||
			p.label.toLowerCase().includes(needle),
	)
}

export const getCloseLoopPlaceholderItems = (
	kind: CloseLoopTemplateKind,
	query: string,
) => filterPlaceholders(CLOSE_LOOP_PLACEHOLDERS[kind], query)

export const insertCloseLoopPlaceholder = (
	editor: Editor,
	range: Range,
	key: string,
) =>
	editor
		.chain()
		.focus()
		.insertContentAt(range, closeLoopPlaceholderToken(key))
		.run()

const TOKEN = /\{\{[A-Za-z]+\}\}/g

// Marks every token so the template reads as text with slots in it.
const tokenHighlight = new Plugin({
	key: new PluginKey("closeLoopPlaceholderHighlight"),
	props: {
		decorations(state) {
			const decorations: Decoration[] = []
			state.doc.descendants((node, pos) => {
				if (!node.isText || !node.text) return
				for (const match of node.text.matchAll(TOKEN)) {
					const from = pos + (match.index ?? 0)
					decorations.push(
						Decoration.inline(from, from + match[0].length, {
							class:
								"rounded bg-gray-100 px-0.5 font-mono text-[12.5px] text-secondary dark:bg-gray-800",
						}),
					)
				}
			})
			return DecorationSet.create(state.doc, decorations)
		},
	},
})

export interface CloseLoopPlaceholderOptions {
	kind: CloseLoopTemplateKind
	placeholders?: CloseLoopPlaceholder[]
	render: SuggestionOptions<CloseLoopPlaceholder, { key: string }>["render"]
}

export const CloseLoopPlaceholderSuggestion =
	Extension.create<CloseLoopPlaceholderOptions>({
		name: "closeLoopPlaceholderSuggestion",

		addOptions() {
			return { kind: "reported", render: closeLoopPlaceholderRenderer }
		},

		addProseMirrorPlugins() {
			return [
				Suggestion<CloseLoopPlaceholder, { key: string }>({
					editor: this.editor,
					char: "{{",
					pluginKey: CloseLoopPlaceholderPluginKey,
					allowSpaces: false,
					// A token can sit right after text, like "Hey {{".
					allowedPrefixes: null,
					items: ({ query }) =>
						filterPlaceholders(
							this.options.placeholders ??
								CLOSE_LOOP_PLACEHOLDERS[this.options.kind],
							query,
						),
					render: this.options.render,
					command: ({ editor, range, props }) =>
						insertCloseLoopPlaceholder(editor, range, props.key),
				}),
				tokenHighlight,
			]
		},
	})
